import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import Strings from '../../../Strings';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { getProgramDisplayName } from '../../../utils/cmsPrograms';
import { toolTipForTable } from '../../../utils';
import { DATE_FORMAT_YEAR_MONTH_DAY } from '../../../constants';
import { isSuperUser, isShipper } from '../../../utils/userRoles';
import { formatMoney, formatTimestamp, INVOICE_STATUSES, normalizeInvoiceComment, parseDate, parseDateYMD, parseMoney } from './helpers';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { EditInvoiceModal, SetInvoiceDateModal } from './modals';
import {
  CHANGE_DEVICE_INVOICE_INITIAL_STATUS,
  CHANGE_DEVICE_INVOICE_RECURRING_STATUS,
  invoiceActions,
  RESTORE_DEVICE_INVOICE_INITIAL,
  RESTORE_DEVICE_INVOICE_RECURRING,
  SET_DEVICE_INVOICE_DATE_ERROR,
  UPDATE_DEVICE_INVOICE_INITIAL_ERROR,
  UPDATE_DEVICE_INVOICE_RECURRING_ERROR,
} from './redux/actions';
import { openModalAction } from '../../../actions/modal';
import { CHANGE_INVOICES_STATUS_MODAL_NAME } from '../../../modals/ChangeInvoicesStatusModal';
import { ActionButton, ActionWidget } from '../../../components/ActionWidget';

const INITIAL_SORTABLE_COLUMN_IDS = new Set([
  'device_id',
  'device_model',
  'shipment_date_from_rxcap',
  'invoice_date',
  'kitting_fee',
  'shipping_fee',
  'customer_name',
  'billing_org_name',
  'provider_name',
  'price',
]);

const RECURRING_SORTABLE_COLUMN_IDS = new Set([
  'billing_month',
  'kit_id',
  'program_display',
  'source_device_id',
  'billing_end_date',
  'invoice_date',
  'customer_name',
  'billing_org_name',
  'provider_name',
  'price',
  'data_fee',
]);

function DeviceInvoicesReviewTable(props) {
  const { data, isLoading, isRecurring, tableName, showNotification, orgs, loadData, status } = props;
  const { pagination, sorting, onOffsetChange, onPageSizeChange, onSortingChange } = props;
  const {
    updateDeviceInvoiceInitial,
    updateDeviceInvoiceRecurring,
    changeDeviceInvoiceInitialStatus,
    changeDeviceInvoiceRecurringStatus,
    restoreDeviceInvoiceInitial,
    restoreDeviceInvoiceRecurring,
    setDeviceInvoiceDate,
    clearDeviceInvoiceDate,
  } = props;
  const { changeInvoicesStatus, handleRecalculate, onNavigate, openConfirmModal } = props;
  const canManageDeviceInvoices = isSuperUser() || isShipper();

  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showSetInvoiceDateModal, setShowSetInvoiceDateModal] = React.useState(false);
  const [rowToEdit, setRowToEdit] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const tableRef = React.useRef(null);

  const defaultSorting = useMemo(() => {
    if (status !== INVOICE_STATUSES.action_required.key) {
      return [];
    }

    if (isRecurring) {
      return [{ id: 'kit_id', desc: false }];
    }

    return [
      { id: 'device_id', desc: false },
      { id: 'shipment_date_from_rxcap', desc: true },
    ];
  }, [isRecurring, status]);

  const refresh = useCallback(() => {
    loadData();
    tableRef.current?.resetSelectedRows();
    setSelectedRows([]);
  }, [loadData]);
  const shippingCostCell = info => {
    const row = info.row.original;
    const value = formatMoney(info.getValue());
    if (!row.order_uuid) {
      return value;
    }
    return (
      <div
        onClick={e => { e.preventDefault(); e.stopPropagation(); onNavigate(`/shipping/order-details/${row.order_uuid}`); }}
        className="clickable"
        title={row.shipment_uuid ? `${Strings.shipping.shipmentId}: ${row.shipment_uuid}` : Strings.shipping.orderId}
      >
        {value}
      </div>
    );
  };

  const onEdit = useCallback((row) => {
    setRowToEdit(row);
    setShowEditModal(true);
  }, []);

  const handleBulkSetInvoiceDate = async (invoiceDate) => {
    const formattedInvoiceDate = parseDateYMD(invoiceDate);
    if (!formattedInvoiceDate) {
      showNotification(Strings.deviceInvoices.invoiceDateRequired || 'Invoice date is required', 3000, true);
      return false;
    }

    const payload = {
      invoice_date: formattedInvoiceDate,
      ...(isRecurring
        ? { recurring_invoice_ids: selectedRows.map(row => row.invoice_uuid) }
        : { initial_invoice_ids: selectedRows.map(row => row.invoice_uuid) }),
    };

    const res = await setDeviceInvoiceDate(payload);
    if (res?.type === SET_DEVICE_INVOICE_DATE_ERROR) {
      showNotification(res?.response?.data?.standard_error?.message || Strings.deviceInvoices.invoiceDateUpdateFailed, 3000, true);
      return false;
    }

    setShowSetInvoiceDateModal(false);
    showNotification(Strings.deviceInvoices.invoiceDateUpdated, 3000, false);
    refresh();
    return true;
  };

  const handleBulkClearInvoiceDate = useCallback(() => {
    const payload = isRecurring
      ? { recurring_invoice_ids: selectedRows.map(row => row.invoice_uuid) }
      : { initial_invoice_ids: selectedRows.map(row => row.invoice_uuid) };

    openConfirmModal({
      title: Strings.deviceInvoices.clearInvoiceDateWarning,
      batchWarning: selectedRows.length === 1
        ? Strings.deviceInvoices.batchActionSingleWarning
        : Strings.formatString(Strings.deviceInvoices.batchActionWarning, selectedRows.length),
      onConfirmAction: () => clearDeviceInvoiceDate(payload),
      confirmPostAction: refresh,
      caption: Strings.deviceInvoices.clearInvoiceDate,
      hideCaution: true,
      onSuccessNotification: Strings.deviceInvoices.invoiceDateCleared,
      onCancelAction: null,
    });
  }, [clearDeviceInvoiceDate, isRecurring, openConfirmModal, refresh, selectedRows]);

  const handleEdit = async (updatedData) => {
    const action = isRecurring ? updateDeviceInvoiceRecurring : updateDeviceInvoiceInitial;
    const originalShipmentDateFromRxcap = parseDate(rowToEdit?.shipment_date_from_rxcap);
    const updatedShipmentDateFromRxcap = parseDate(updatedData?.shipment_date_from_rxcap);
    const shipmentDateFromRxcapChanged = updatedShipmentDateFromRxcap !== originalShipmentDateFromRxcap;
    const payload = isRecurring ? {
      price: parseMoney(updatedData?.price),
      data_fee: parseMoney(updatedData?.data_fee),
      provider_id: updatedData?.provider_id || undefined,
      clear_provider_id: Boolean(rowToEdit?.provider_id && !updatedData?.provider_id),
      billing_end_date: parseDateYMD(updatedData?.billing_end_date),
      clear_billing_end_date: Boolean(rowToEdit?.billing_end_date && !updatedData?.billing_end_date),
      comment: normalizeInvoiceComment(updatedData?.comment),
      status: updatedData?.billing_status !== rowToEdit?.billing_status ? updatedData?.billing_status : undefined,
    } : {
      price: parseMoney(updatedData?.price),
      kitting_fee: parseMoney(updatedData?.kitting_fee),
      shipping_fee: parseMoney(updatedData?.shipping_fee),
      provider_id: updatedData?.provider_id || undefined,
      clear_provider_id: Boolean(rowToEdit?.provider_id && !updatedData?.provider_id),
      invoice_date: parseDateYMD(updatedData?.invoice_date),
      clear_invoice_date: Boolean(rowToEdit?.invoice_date && !updatedData?.invoice_date),
      ...(shipmentDateFromRxcapChanged ? { shipment_date_from_rxcap: updatedShipmentDateFromRxcap } : {}),
      clear_shipment_date_from_rxcap: shipmentDateFromRxcapChanged && Boolean(rowToEdit?.shipment_date_from_rxcap && !updatedData?.shipment_date_from_rxcap),
      comment: normalizeInvoiceComment(updatedData?.comment),
      status: updatedData?.billing_status !== rowToEdit?.billing_status ? updatedData?.billing_status : undefined,
    };
    const res = await action(rowToEdit.invoice_uuid, payload);
    if (res?.type === UPDATE_DEVICE_INVOICE_INITIAL_ERROR || res?.type === UPDATE_DEVICE_INVOICE_RECURRING_ERROR) {
      showNotification(res?.response?.data?.standard_error?.message || Strings.deviceInvoices.invoiceUpdateFailed, 3000, true);
      return false;
    }
    setShowEditModal(false);
    showNotification(Strings.deviceInvoices.invoiceUpdated, 3000, false);
    loadData();
    return true;
  }

  const changeStatus = useCallback((rows, targetStatus, successMessage, title) => {
    const action = isRecurring ? changeDeviceInvoiceRecurringStatus : changeDeviceInvoiceInitialStatus;
    const actionType = isRecurring ? CHANGE_DEVICE_INVOICE_RECURRING_STATUS : CHANGE_DEVICE_INVOICE_INITIAL_STATUS;
    const statusFrom = INVOICE_STATUSES[rows[0].billing_status]?.name || rows[0].billing_status;
    const statusTo = INVOICE_STATUSES[targetStatus]?.name || targetStatus;
    const text = targetStatus === INVOICE_STATUSES.approved.key
      ? `You are going to change the status of the selected invoices from "${statusFrom}" to "${statusTo}". Please select invoice date.`
      : `Are you sure you want to change the status of the selected invoices from "${statusFrom}" to "${statusTo}"?`;
    changeInvoicesStatus({
      rows,
      targetStatus,
      action,
      actionType,
      successMessage,
      text,
      title,
      onSuccess: refresh,
    });
  }, [changeDeviceInvoiceInitialStatus, changeDeviceInvoiceRecurringStatus, changeInvoicesStatus, isRecurring, refresh]);

  const handleCancelInvoices = useCallback((rows) => {
    changeStatus(
      rows,
      INVOICE_STATUSES.canceled.key,
      Strings.deviceInvoices.invoicesCanceled,
      Strings.deviceInvoices.cancelInvoices,
    );
  }, [changeStatus]);

  const handleMarkReady = useCallback((rows) => {
    changeStatus(
      rows,
      INVOICE_STATUSES.ready_for_approval.key,
      Strings.deviceInvoices.invoicesMarkedReady,
      Strings.deviceInvoices.markInvoicesReady,
    );
  }, [changeStatus]);

  const handleApproveInvoices = useCallback((rows) => {
    changeStatus(
      rows,
      INVOICE_STATUSES.approved.key,
      Strings.deviceInvoices.invoicesApproved,
      Strings.deviceInvoices.approveInvoices,
    );
  }, [changeStatus]);

  const handleRequestAction = useCallback((rows) => {
    changeStatus(
      rows,
      INVOICE_STATUSES.action_required.key,
      Strings.deviceInvoices.actionRequested,
      Strings.deviceInvoices.requestAction,
    );
  }, [changeStatus]);

  const handleRestoreInvoices = useCallback((rows) => {
    const action = isRecurring ? restoreDeviceInvoiceRecurring : restoreDeviceInvoiceInitial;
    const actionType = isRecurring ? RESTORE_DEVICE_INVOICE_RECURRING : RESTORE_DEVICE_INVOICE_INITIAL;
    changeInvoicesStatus({
      rows,
      action,
      actionType,
      successMessage: Strings.deviceInvoices.invoicesRestored,
      text: Strings.deviceInvoices.restoreInvoicesWarning,
      title: Strings.deviceInvoices.restoreInvoices,
      onSuccess: refresh,
    });
  }, [changeInvoicesStatus, isRecurring, refresh, restoreDeviceInvoiceInitial, restoreDeviceInvoiceRecurring]);

  const columns = useMemo(() => {
    const showInvoiceDateColumn = status === INVOICE_STATUSES.approved.key;
    const sortableColumnIds = isRecurring ? RECURRING_SORTABLE_COLUMN_IDS : INITIAL_SORTABLE_COLUMN_IDS;
    const sortingOptions = columnId => sortableColumnIds.has(columnId) ? {} : { enableSorting: false };
    const formatBoolean = value => value ? Strings.yes : Strings.no;
    const formatComments = comments => {
      if (!Array.isArray(comments) || comments.length === 0) {
        return '-';
      }
      return toolTipForTable(comments.join(' | '), comments);
    };

    const invoiceUuidColumn = columnHelper.accessor('invoice_uuid', {
      header: Strings.deviceInvoices.invoiceUuid || 'Invoice UUID',
      cell: info => info.getValue() || '-',
      size: 280,
      ...sortingOptions('invoice_uuid'),
    });

    const sharedColumns = [
      invoiceUuidColumn,
      columnHelper.accessor('customer_name', {
        header: Strings.customer,
        cell: info => info.getValue() || '-',
        size: 220,
      }),
      columnHelper.accessor('billing_org_name', {
        header: Strings.deviceInvoices.billingOrg,
        cell: info => info.getValue() || '-',
        size: 220,
      }),
      columnHelper.accessor('provider_name', {
        header: Strings.capPatient.provider,
        cell: info => info.getValue() || '-',
        size: 220,
      }),
      columnHelper.accessor('price', {
        header: Strings.deviceInvoices.price,
        cell: info => formatMoney(info.getValue()),
        size: 120,
      }),
      columnHelper.accessor('issues', {
        header: Strings.issues,
        cell: info => {
          if (!info.getValue() || info.getValue().length === 0) return '-';
          const issues = info.getValue()?.map(issue => Strings.deviceInvoices.invoiceIssuesEnum[issue] || issue) || [];
          return issues.length ? toolTipForTable(issues.join(', '), issues) : '-';
        },
        size: 220,
        ...sortingOptions('issues'),
      }),
      columnHelper.accessor('comments', {
        header: Strings.comments,
        cell: info => formatComments(info.getValue()),
        size: 260,
        ...sortingOptions('comments'),
      }),
    ];

    if (isRecurring) {
      const recurringColumns = [
        columnHelper.accessor('billing_month', {
          header: Strings.deviceInvoices.billingMonth,
          cell: info => formatTimestamp(info.getValue(), 'YYYY-MM'),
          size: 140,
        }),
        columnHelper.accessor('kit_id', {
          header: Strings.kitId,
          cell: info => info.getValue() || '-',
          size: 140,
        }),
        columnHelper.accessor('program_display', {
          cell: info => info.row.original.program ? getProgramDisplayName(info.row.original.program, info.row.original.subprogram || info.row.original.program) : '-',
          header: Strings.program,
          size: 220,
        }),
        columnHelper.accessor('source_device_id', {
          header: Strings.deviceInvoices.sourceDeviceId,
          cell: info => info.getValue() || '-',
          size: 160,
        }),
        columnHelper.accessor('billing_end_date', {
          header: Strings.deviceInvoices.billingEndDate,
          cell: info => formatTimestamp(info.getValue()),
          sortingFn: 'dateSortingFn',
          size: 140,
        }),
        columnHelper.accessor('devices', {
          header: Strings.devices,
          cell: info => info.getValue() ? toolTipForTable(info.getValue().join(', '), info.getValue()) : '-',
          size: 240,
          ...sortingOptions('devices'),
        }),
        columnHelper.accessor('is_sample', {
          header: Strings.deviceInvoices.isSample,
          cell: info => formatBoolean(info.getValue()),
          size: 120,
          ...sortingOptions('is_sample'),
        }),
        columnHelper.accessor('data_fee', {
          header: Strings.deviceInvoices.dataFee,
          cell: info => formatMoney(info.getValue()),
          size: 120,
        }),
        ...sharedColumns,
      ];
      if (showInvoiceDateColumn) {
        recurringColumns.splice(1, 0, columnHelper.accessor('invoice_date', {
          header: Strings.deviceInvoices.invoiceDate,
          cell: info => formatTimestamp(info.getValue(), DATE_FORMAT_YEAR_MONTH_DAY),
          sortingFn: 'dateSortingFn',
          size: 140,
        }));
      }
      return recurringColumns;
    }

    const initialColumns = [
      columnHelper.accessor('device_id', {
        header: Strings.deviceId,
        cell: info => info.getValue() || '-',
        size: 160,
      }),
      columnHelper.accessor('device_model', {
        header: Strings.deviceModel,
        cell: info => {
          const row = info.row.original;
          return `${row.device_manufacturer || ''} ${row.device_model || ''}`.trim() || '-';
        },
        size: 220,
      }),
      columnHelper.accessor('shipment_date_from_rxcap', {
        header: Strings.shipping.shipmentDate,
        cell: info => formatTimestamp(info.getValue()),
        sortingFn: 'dateSortingFn',
        size: 150,
      }),
      columnHelper.accessor('shipping_fee', {
        header: Strings.shipping.shippingCost,
        cell: info => shippingCostCell(info),
        size: 130,
      }),
      columnHelper.accessor('kitting_fee', {
        header: Strings.deviceInvoices.kittingFee,
        cell: info => formatMoney(info.getValue()),
        size: 130,
      }),
      columnHelper.accessor('is_sample', {
        header: Strings.deviceInvoices.isSample,
        cell: info => formatBoolean(info.getValue()),
        size: 120,
        ...sortingOptions('is_sample'),
      }),
      ...sharedColumns,
    ];
    if (showInvoiceDateColumn) {
      initialColumns.splice(2, 0, columnHelper.accessor('invoice_date', {
        header: Strings.deviceInvoices.invoiceDate,
        cell: info => formatTimestamp(info.getValue(), DATE_FORMAT_YEAR_MONTH_DAY),
        sortingFn: 'dateSortingFn',
        size: 140,
      }));
    }
    return initialColumns;
  }, [isRecurring, status]);

  const tableButtons = useMemo(() => {
    if (!canManageDeviceInvoices) {
      return [];
    }
    const ret = [
      {
        icon: 'edit',
        text: Strings.edit,
        onClick: (_, row) => onEdit(row),
      },
    ];
    if (status === INVOICE_STATUSES.action_required.key) {
      ret.push(
        {
          icon: 'check',
          text: Strings.deviceInvoices.markReady,
          onClick: (_, row) => handleMarkReady([row]),
        },
      );
      ret.push(
        {
          icon: 'refresh',
          text: Strings.deviceInvoices.recalculateInvoice,
          onClick: (_, row) => handleRecalculate([row]),
        },
      );
    }
    if (status === INVOICE_STATUSES.ready_for_approval.key) {
      ret.push(
        {
          icon: 'refresh',
          text: Strings.deviceInvoices.recalculateInvoice,
          onClick: (_, row) => handleRecalculate([row]),
        },
      );
      ret.push(
        {
          icon: 'reminder',
          text: Strings.deviceInvoices.requestAction,
          onClick: (_, row) => handleRequestAction([row]),
        },
      );
      ret.push(
        {
          icon: 'check',
          text: Strings.deviceInvoices.approveInvoices,
          onClick: (_, row) => handleApproveInvoices([row]),
        },
      );
    }
    if (status !== INVOICE_STATUSES.canceled.key) {
      ret.push(
        {
          icon: 'delete',
          text: Strings.deviceInvoices.cancelInvoices,
          onClick: (_, row) => handleCancelInvoices([row]),
        },
      );
    } else {
      ret.push(
        {
          icon: 'refresh',
          text: Strings.deviceInvoices.restoreInvoice,
          onClick: (_, row) => handleRestoreInvoices([row]),
        },
      );
    }
    return ret;
  }, [
    handleApproveInvoices,
    handleCancelInvoices,
    handleMarkReady,
    handleRecalculate,
    handleRequestAction,
    handleRestoreInvoices,
    canManageDeviceInvoices,
    onEdit,
    status,
  ]);

  return (
    <>
      <Table3
        ref={tableRef}
        isLoading={isLoading}
        name={tableName}
        data={data}
        columns={columns}
        sorting={sorting || defaultSorting}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        onRowSelectionChange={canManageDeviceInvoices ? setSelectedRows : undefined}
        buttons={tableButtons}
        enableColumnFiltering
        onRowClick={(row) => onNavigate(`/device-invoices/invoice-details/${isRecurring ? 'recurring' : 'initial'}/${row.invoice_uuid}`, { invoice: row })}
      />
      <ActionWidget show={canManageDeviceInvoices && selectedRows?.length > 0}>
        {status !== INVOICE_STATUSES.canceled.key && (
          <ActionButton
            img="action-delete"
            tooltiptext={Strings.deviceInvoices.cancelInvoices}
            text={Strings.deviceInvoices.cancelInvoices}
            action={() => handleCancelInvoices(selectedRows)}
          />
        )}
        {status === INVOICE_STATUSES.canceled.key && (
          <ActionButton
            img="action-reset"
            tooltiptext={Strings.deviceInvoices.restoreInvoices}
            text={Strings.deviceInvoices.restoreInvoices}
            action={() => handleRestoreInvoices(selectedRows)}
          />
        )}
        {status === INVOICE_STATUSES.ready_for_approval.key && (
          <ActionButton
            img="action-reminder"
            tooltiptext={Strings.deviceInvoices.requestAction}
            text={Strings.deviceInvoices.requestAction}
            action={() => handleRequestAction(selectedRows)}
          />
        )}

        {status === INVOICE_STATUSES.action_required.key && (
          <ActionButton
            img="action-check"
            tooltiptext={Strings.deviceInvoices.markInvoicesReady}
            text={Strings.deviceInvoices.markInvoicesReady}
            action={() => handleMarkReady(selectedRows)}
          />
        )}
        {[INVOICE_STATUSES.action_required.key, INVOICE_STATUSES.ready_for_approval.key].includes(status) && (
          <ActionButton
            img="action-reset"
            tooltiptext={Strings.deviceInvoices.recalculateInvoices}
            text={Strings.deviceInvoices.recalculateInvoices}
            action={() => {handleRecalculate(selectedRows); setSelectedRows([]); tableRef.current?.resetSelectedRows();}}
          />
        )}
        {status === INVOICE_STATUSES.ready_for_approval.key && (
          <ActionButton
            img="action-check"
            tooltiptext={Strings.deviceInvoices.approveInvoices}
            text={Strings.deviceInvoices.approveInvoices}
            action={() => handleApproveInvoices(selectedRows)}
          />
        )}
        {status === INVOICE_STATUSES.approved.key && (isSuperUser() || isShipper()) && (
          <>
            <ActionButton
              img="action-edit"
              tooltiptext={Strings.deviceInvoices.changeInvoiceDate}
              text={Strings.deviceInvoices.changeInvoiceDate}
              action={() => setShowSetInvoiceDateModal(true)}
            />
            <ActionButton
              img="action-reset"
              tooltiptext={Strings.deviceInvoices.clearInvoiceDate}
              text={Strings.deviceInvoices.clearInvoiceDate}
              action={handleBulkClearInvoiceDate}
            />
          </>
        )}
      </ActionWidget>
      {showEditModal && rowToEdit && (
        <EditInvoiceModal
          invoice={rowToEdit}
          isRecurring={isRecurring}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEdit}
          organizations={orgs}
        />
      )}
      {showSetInvoiceDateModal && (
        <SetInvoiceDateModal
          onClose={() => setShowSetInvoiceDateModal(false)}
          onSubmit={handleBulkSetInvoiceDate}
          title={Strings.deviceInvoices.changeInvoiceDate}
        />
      )}
    </>
  );
}

DeviceInvoicesReviewTable.propTypes = {
  data: PropTypes.array,
  changeDeviceInvoiceInitialStatus: PropTypes.func.isRequired,
  changeDeviceInvoiceRecurringStatus: PropTypes.func.isRequired,
  changeInvoicesStatus: PropTypes.func.isRequired,
  clearDeviceInvoiceDate: PropTypes.func.isRequired,
  handleRecalculate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isRecurring: PropTypes.bool,
  loadData: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onOffsetChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onSortingChange: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  orgs: PropTypes.arrayOf(PropTypes.shape({})),
  pagination: PropTypes.shape({}),
  restoreDeviceInvoiceInitial: PropTypes.func.isRequired,
  restoreDeviceInvoiceRecurring: PropTypes.func.isRequired,
  setDeviceInvoiceDate: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  sorting: PropTypes.arrayOf(PropTypes.shape({})),
  status: PropTypes.string.isRequired,
  tableName: PropTypes.string,
  updateDeviceInvoiceInitial: PropTypes.func.isRequired,
  updateDeviceInvoiceRecurring: PropTypes.func.isRequired,
};

DeviceInvoicesReviewTable.defaultProps = {
  data: [],
  isLoading: false,
  isRecurring: false,
  orgs: [],
  pagination: {},
  sorting: [],
  tableName: undefined,
};

const mapDispatchToProps = dispatch => ({
  changeDeviceInvoiceInitialStatus: data => dispatch(invoiceActions.changeDeviceInvoiceInitialStatus(data)),
  changeDeviceInvoiceRecurringStatus: data => dispatch(invoiceActions.changeDeviceInvoiceRecurringStatus(data)),
  restoreDeviceInvoiceInitial: data => dispatch(invoiceActions.restoreDeviceInvoiceInitial(data)),
  restoreDeviceInvoiceRecurring: data => dispatch(invoiceActions.restoreDeviceInvoiceRecurring(data)),
  changeInvoicesStatus: (data) => dispatch(openModalAction(CHANGE_INVOICES_STATUS_MODAL_NAME, data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  // commentDeviceInvoiceInitial: data => dispatch(invoiceActions.commentDeviceInvoiceInitial(data)),
  // commentDeviceInvoiceRecurring: data => dispatch(invoiceActions.commentDeviceInvoiceRecurring(data)),
  updateDeviceInvoiceInitial: (invoiceUuid, data) => dispatch(invoiceActions.updateDeviceInvoiceInitial(invoiceUuid, data)),
  updateDeviceInvoiceRecurring: (invoiceUuid, data) => dispatch(invoiceActions.updateDeviceInvoiceRecurring(invoiceUuid, data)),
  setDeviceInvoiceDate: data => dispatch(invoiceActions.setDeviceInvoiceDate(data)),
  clearDeviceInvoiceDate: data => dispatch(invoiceActions.clearDeviceInvoiceDate(data)),
  // openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
  onNavigate: (path, state) => dispatch(push(path, state)),
});

export default connect(null, mapDispatchToProps)(DeviceInvoicesReviewTable);
